import { ActionIcon, Group, Skeleton, Text, Tooltip } from '@mantine/core';
import { IconFocus2 } from '@tabler/icons-react';
import { featureCollection } from '@turf/turf';
import { MapEvent } from 'mapbox-gl';
import { FC, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { MapRef, NavigationControl } from 'react-map-gl';

import { FieldAnswer } from '../../../lib/types/validation';
import classes from '../page.module.css';

import { PfMap, PlotsLayer, zoomOnFeature } from '@map';

interface FieldMapProps {
  currentField?: FieldAnswer;
}

export const FieldMap: FC<FieldMapProps> = ({ currentField }) => {
  const { t } = useTranslation();
  const mapRef = useRef<MapRef>(null);

  const zoomToCurrentField = useCallback(() => {
    if (!currentField?.field || !mapRef.current) {
      return;
    }

    zoomOnFeature({
      map: mapRef.current.getMap(),
      geojson: currentField.field,
    });
  }, [currentField]);

  const handleLoad = useCallback(
    (map: MapEvent) => {
      if (!currentField?.field) {
        return;
      }

      zoomOnFeature({
        map: map.target,
        geojson: currentField.field,
      });
    },
    [currentField],
  );

  useEffect(() => {
    zoomToCurrentField();
  }, [zoomToCurrentField]);

  return (
    <>
      <Group align="center" justify="space-between" mb="sm" w="100%" wrap="nowrap">
        <Text fw="bold" lineClamp={1} size="lg" style={{ flex: 1 }}>
          {currentField?.field.properties.name}
        </Text>
        <Group gap="xs" wrap="nowrap">
          <Tooltip label={t('farmer-validation.map-zoom-field')}>
            <ActionIcon
              aria-label={t('farmer-validation.map-zoom-field')}
              onClick={zoomToCurrentField}
              size="lg"
              variant="light"
            >
              <IconFocus2 size={18} stroke={1.5} />
            </ActionIcon>
          </Tooltip>
          <Text fw="bold" style={{ whiteSpace: 'nowrap' }}>
            {currentField?.field.properties.area.toFixed(2)} ha
          </Text>
        </Group>
      </Group>
      {currentField ? (
        <PfMap
          classnames={{
            container: classes.mapContainer,
          }}
          onLoad={handleLoad}
          ref={mapRef}
        >
          <NavigationControl showCompass={false} />
          <PlotsLayer data={featureCollection([currentField.field])} />
        </PfMap>
      ) : (
        <Skeleton height={300} />
      )}
    </>
  );
};
